/**
 * App-level error boundary. Catches uncaught React errors in the tree and shows
 * a simple fallback with a reload option. Reports to Sentry when configured.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { Button } from '@/components/ui/button';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    Sentry.captureException(error, {
      extra: { componentStack: errorInfo.componentStack },
    });
    if (typeof console !== 'undefined' && console.error) {
      console.error('[ErrorBoundary]', error, errorInfo.componentStack);
    }
  }

  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 p-6 text-center">
          <p className="text-sm text-muted-foreground">
            Something went wrong. Reload the page to try again.
          </p>
          <Button onClick={this.handleReload} variant="outline" size="sm">
            Reload
          </Button>
        </div>
      );
    }
    return this.props.children;
  }
}
